import { useState, useEffect } from 'react';
import axios from 'axios';
import MainLayout from '../components/MainLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('add');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'claimant',
    phone: '',
    designation: '',
    pan: '',
    gst: '',
    address: '',
    products: '',
    short_name: '',
    claimant_id: '',
    signature_url: ''
  });
  const [signatureFile, setSignatureFile] = useState(null);
  const [claimants, setClaimants] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClaimants();
    if (activeTab === 'list') {
      fetchUsers();
    }
  }, [activeTab]);

  const fetchClaimants = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/claimants`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClaimants(res.data.claimants);
    } catch (err) {
      console.error('Failed to fetch claimants:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSignatureFile(e.target.files[0]);
    }
  };

  const uploadSignature = async () => {
    if (!signatureFile) return null;

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', signatureFile);

      const res = await axios.post(`${API_URL}/files/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      if (res.data.status === 'success') {
        return res.data.file_url;
      }
    } catch (err) {
      console.error('File upload failed:', err);
      throw new Error('Failed to upload signature file: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      let signatureKey = formData.signature_url;

      if (signatureFile) {
        signatureKey = await uploadSignature();
      }

      const payload = { ...formData, signature_url: signatureKey };
      
      // Clean payload: Remove empty strings
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
          delete payload[key];
        }
      });

      await axios.post(`${API_URL}/admin/users`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage('User created successfully! Credentials sent via email.');
      setFormData({
        name: '',
        email: '',
        role: 'claimant',
        phone: '',
        designation: '',
        pan: '',
        gst: '',
        address: '',
        products: '',
        short_name: '',
        claimant_id: '',
        signature_url: ''
      });
      setSignatureFile(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout user={user} onLogout={onLogout}>
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <p className="page-description">Add and manage users</p>
      </div>

        <div className="card">
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', borderBottom: '2px solid #e2e8f0' }}>
            <button
              data-testid="add-user-tab"
              onClick={() => setActiveTab('add')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'add' ? '3px solid #667eea' : 'none',
                color: activeTab === 'add' ? '#667eea' : '#718096',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Add User
            </button>
            <button
              data-testid="list-users-tab"
              onClick={() => setActiveTab('list')}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'none',
                border: 'none',
                borderBottom: activeTab === 'list' ? '3px solid #667eea' : 'none',
                color: activeTab === 'list' ? '#667eea' : '#718096',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              List Users
            </button>
          </div>

          {activeTab === 'add' ? (
            <>
              {message && <div className="success-message">{message}</div>}
              {error && <div className="error-message">{error}</div>}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Role</label>
                  <select
                    data-testid="role-select"
                    className="form-select"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="claimant">Claimant</option>
                    <option value="arbitrator">Arbitrator</option>
                    <option value="authorized_representative">Authorized Representative</option>
                    <option value="tribunal_secretary">Tribunal Secretary</option>
                  </select>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      data-testid="name-input"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      data-testid="email-input"
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    data-testid="phone-input"
                    className="form-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Signature Upload</label>
                  <input
                    type="file"
                    className="form-input"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                  />
                  <small style={{ color: '#718096' }}>Upload signature image or PDF</small>
                </div>

                {formData.role === 'claimant' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label>Designation</label>
                        <input
                          data-testid="designation-input"
                          className="form-input"
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Short Name</label>
                        <input
                          data-testid="short-name-input"
                          className="form-input"
                          value={formData.short_name}
                          onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                          required
                        />
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label>PAN</label>
                        <input
                          className="form-input"
                          value={formData.pan}
                          onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                        />
                      </div>
                      <div className="form-group">
                        <label>GST</label>
                        <input
                          className="form-input"
                          value={formData.gst}
                          onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Address</label>
                      <textarea
                        className="form-input"
                        rows="2"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Products</label>
                      <input
                        className="form-input"
                        value={formData.products}
                        onChange={(e) => setFormData({ ...formData, products: e.target.value })}
                      />
                    </div>
                  </>
                )}

                {formData.role === 'authorized_representative' && (
                  <div className="form-group">
                    <label>Select Claimant</label>
                    <select
                      data-testid="claimant-select"
                      className="form-select"
                      value={formData.claimant_id}
                      onChange={(e) => setFormData({ ...formData, claimant_id: e.target.value })}
                      required
                    >
                      <option value="">Select a claimant</option>
                      {claimants.map((c) => (
                        <option key={c._id} value={c._id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <button
                  data-testid="create-user-button"
                  type="submit"
                  className="btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Creating...' : 'Create User'}
                </button>
              </form>
            </>
          ) : (
            <>
              {users.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-text">No users found</div>
                </div>
              ) : (
                <table data-testid="users-table" className="data-table">
                  <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Phone</th>
                        <th>Actual Password</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.email}>
                          <td>{u.name}</td>
                          <td>{u.email}</td>
                          <td style={{ fontWeight: 600 }}>
                            <span className="badge badge-new">
                              {u.role.replace('_', ' ').toUpperCase()}
                            </span>
                          </td>
                          <td>{u.phone || '-'}</td>
                          <td style={{ color: '#e53e3e', fontWeight: 600, fontFamily: 'monospace' }}>
                            {u.actualpassword || 'N/A'}
                          </td>
                          <td>{new Date(u.created_at).toLocaleDateString()}</td>
                        </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
    </MainLayout>
  );
};

export default AdminDashboard;
